import { isNil, uniq, without } from 'lodash-es';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import {
  type BrandKey,
  type BrandTheme,
  type EnvConfigType,
  EnvironmentType,
  type RuntimeEndpointsType,
  type RuntimeEndpointType,
} from '@/models/configuration';
import { CONFIGURATION_STORAGE_KEY } from '@/utils/constants';
import { timeoutPromise } from '@/utils/promise';

export interface ConfigurationState {
  isHydrated: boolean;
  envConfig: EnvConfigType;
  endpoint: RuntimeEndpointType;
  backupEndpoints: RuntimeEndpointsType;

  setEnvConfig: (envConfig: EnvConfigType) => void;
  setEndpoint: (endpoint: Partial<RuntimeEndpointType>) => void;
  setBackupEndpoints: (backupEndpoints: Partial<RuntimeEndpointsType>) => void;
  setBrand: (key: BrandKey, theme: BrandTheme) => void;
  setTheme: (theme: BrandTheme) => void;
  setHydrated: () => void;

  removeBackupEndpoints: (type: keyof RuntimeEndpointsType, endpoint: string) => void;
}

export const useConfigurationStore = create<ConfigurationState>()(
  persist(
    immer(
      (set, get): ConfigurationState => ({
        isHydrated: false,
        envConfig: {
          env: EnvironmentType.LOCAL,
          brand: null,
          config: null,
          sentry: {
            dsn: '',
            enable: false,
            environment: 'local',
            release: '',
          },

          defaultEndpointUrl: [],
          defaultWebSocketEndpointUrl: [],
          defaultFileEndpointUrl: [],
          defaultFileUploadEndpointUrl: [],
          dynamicEndpoints: [],
          dynamicEndpointExternalUrl: '',
        },
        endpoint: {
          API_ENDPOINT: '',
          WEBSOCKET_ENDPOINT: '',
          FILE_ENDPOINT: '',
          FILEUPLOAD_ENDPOINT: '',
        },
        backupEndpoints: {
          API_ENDPOINTS: [],
          WEBSOCKET_ENDPOINTS: [],
          FILE_ENDPOINTS: [],
          FILEUPLOAD_ENDPOINTS: [],
        } as RuntimeEndpointsType,

        setEnvConfig: (envConfig) => {
          set({ envConfig });

          set((state) => {
            state.envConfig = envConfig;
            state.backupEndpoints.API_ENDPOINTS = envConfig.defaultEndpointUrl;
            state.backupEndpoints.WEBSOCKET_ENDPOINTS = envConfig.defaultWebSocketEndpointUrl;
            state.backupEndpoints.FILE_ENDPOINTS = envConfig.defaultFileEndpointUrl;
            state.backupEndpoints.FILEUPLOAD_ENDPOINTS = envConfig.defaultFileUploadEndpointUrl;
          });
        },
        setEndpoint: (endpoint) =>
          set((draft) => {
            if (!draft.endpoint) {
              draft.endpoint = {
                API_ENDPOINT: endpoint.API_ENDPOINT ?? '',
                WEBSOCKET_ENDPOINT: endpoint.WEBSOCKET_ENDPOINT ?? '',
                FILE_ENDPOINT: endpoint.FILE_ENDPOINT ?? '',
                FILEUPLOAD_ENDPOINT: endpoint.FILEUPLOAD_ENDPOINT ?? '',
              };

              return draft;
            }

            if (!isNil(endpoint.API_ENDPOINT)) {
              draft.endpoint.API_ENDPOINT = endpoint.API_ENDPOINT;
            }

            if (!isNil(endpoint.WEBSOCKET_ENDPOINT)) {
              draft.endpoint.WEBSOCKET_ENDPOINT = endpoint.WEBSOCKET_ENDPOINT;
            }

            if (!isNil(endpoint.FILE_ENDPOINT)) {
              draft.endpoint.FILE_ENDPOINT = endpoint.FILE_ENDPOINT;
            }

            if (!isNil(endpoint.FILEUPLOAD_ENDPOINT)) {
              draft.endpoint.FILEUPLOAD_ENDPOINT = endpoint.FILEUPLOAD_ENDPOINT;
            }
          }),
        setBackupEndpoints: (backupEndpoints) => {
          set((state) => {
            if (backupEndpoints.API_ENDPOINTS) {
              state.backupEndpoints.API_ENDPOINTS = uniq([
                ...state.backupEndpoints.API_ENDPOINTS,
                ...backupEndpoints.API_ENDPOINTS,
              ]);
            }
            if (backupEndpoints.WEBSOCKET_ENDPOINTS) {
              state.backupEndpoints.WEBSOCKET_ENDPOINTS = uniq([
                ...state.backupEndpoints.WEBSOCKET_ENDPOINTS,
                ...backupEndpoints.WEBSOCKET_ENDPOINTS,
              ]);
            }
            if (backupEndpoints.FILE_ENDPOINTS) {
              state.backupEndpoints.FILE_ENDPOINTS = uniq([
                ...state.backupEndpoints.FILE_ENDPOINTS,
                ...backupEndpoints.FILE_ENDPOINTS,
              ]);
            }
            if (backupEndpoints.FILEUPLOAD_ENDPOINTS) {
              state.backupEndpoints.FILEUPLOAD_ENDPOINTS = uniq([
                ...state.backupEndpoints.FILEUPLOAD_ENDPOINTS,
                ...backupEndpoints.FILEUPLOAD_ENDPOINTS,
              ]);
            }
          });
        },
        setBrand: (key, theme) => {
          set((state) => {
            if (state.envConfig.brand) {
              state.envConfig.brand.key = key;
              state.envConfig.brand.theme = theme;
            }
          });
        },
        setTheme: (theme) => {
          set((state) => {
            if (state.envConfig.brand) {
              state.envConfig.brand.theme = theme;
            }
          });
        },
        setHydrated: () => set({ isHydrated: true }),

        removeBackupEndpoints: (type, endpoint) => {
          set((state) => {
            state.backupEndpoints[type] = without(get().backupEndpoints[type], endpoint);

            switch (type) {
              case 'API_ENDPOINTS':
                state.envConfig.defaultEndpointUrl = without(get().envConfig.defaultEndpointUrl, endpoint);
                break;
              case 'FILE_ENDPOINTS':
                state.envConfig.defaultFileEndpointUrl = without(get().envConfig.defaultFileEndpointUrl, endpoint);
                break;
              case 'FILEUPLOAD_ENDPOINTS':
                state.envConfig.defaultFileUploadEndpointUrl = without(
                  get().envConfig.defaultFileUploadEndpointUrl,
                  endpoint,
                );
                break;
              case 'WEBSOCKET_ENDPOINTS':
                state.envConfig.defaultWebSocketEndpointUrl = without(
                  get().envConfig.defaultWebSocketEndpointUrl,
                  endpoint,
                );
                break;
            }
          });
        },
      }),
    ),
    {
      name: CONFIGURATION_STORAGE_KEY,
      version: 8,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        envConfig: state.envConfig,
        endpoint: state.endpoint,
        backupEndpoints: state.backupEndpoints,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHydrated();
      },
      migrate: () => ({
        envConfig: {
          env: EnvironmentType.LOCAL,
          brand: null,
          config: null,
          sentry: {
            dsn: '',
            enable: false,
            environment: 'local',
            release: '',
          },
          defaultEndpointUrl: [],
          defaultWebSocketEndpointUrl: [],
          defaultFileEndpointUrl: [],
          defaultFileUploadEndpointUrl: [],
          dynamicEndpoints: [],
          dynamicEndpointExternalUrl: '',
        },
        endpoint: {
          API_ENDPOINT: '',
          WEBSOCKET_ENDPOINT: '',
          FILE_ENDPOINT: '',
          FILEUPLOAD_ENDPOINT: '',
        },
        backupEndpoints: {
          API_ENDPOINTS: [],
          WEBSOCKET_ENDPOINTS: [],
          FILE_ENDPOINTS: [],
          FILEUPLOAD_ENDPOINTS: [],
        },
      }),
    },
  ),
);

export const setEndpoint = useConfigurationStore.getState().setEndpoint;
export const setEnvConfig = useConfigurationStore.getState().setEnvConfig;
export const setBackupEndpoints = useConfigurationStore.getState().setBackupEndpoints;
export const removeBackupEndpoints = useConfigurationStore.getState().removeBackupEndpoints;

export function promisifyConfigurationHydration(): Promise<void> {
  return timeoutPromise(
    () =>
      new Promise<void>((resolve) => {
        if (useConfigurationStore.getState().isHydrated) {
          resolve();
        } else {
          const unsubscribe = useConfigurationStore.subscribe(({ isHydrated }) => {
            if (isHydrated) {
              unsubscribe();
              resolve();
            }
          });
        }
      }),
    1000,
    () => {},
  );
}
